import { useState } from 'react';
import useStory from './useStory';
import { HORIZON, RACE_HOURS, MONTHS, DAYS, LABEL_HOURS, RATE, inr100, labelPayback, raceCaptions } from '../../data/guideStars';
import { Explainer, Choice } from './GuideParts';

// GUIDE 4'S ANIMATION (rebuilt 20 Oct 2026 from the old PaybackRace): two
// lines of money over ten years, each AC's price today plus the electricity
// it has used so far. The 5 Star starts higher and climbs slower; where the
// lines cross, its extra price has come back. One choice only: hours a day.
// No axes; the two totals sit in plain cards under the drawing.
//
// Props: pair ({ three, five } from pickGuideModels() at 1.5 ton), watch,
// sources.

const W = 340; const H = 170; const X0 = 10; const X1 = W - 10; const Y0 = 16; const Y1 = H - 24;

export default function CostRace({ pair, watch, sources }) {
  const [hours, setHours] = useState(8);
  const story = useStory(16000);
  const yearUnits = (x) => (x.model.annualKwh * hours * DAYS * MONTHS) / LABEL_HOURS;
  const at = (y) => ({ three: pair.three.price + y * yearUnits(pair.three) * RATE, five: pair.five.price + y * yearUnits(pair.five) * RATE });
  const p = labelPayback(pair, hours);
  const cross = Number.isFinite(p.years) && p.years <= HORIZON ? p.years : null;
  const end = at(HORIZON);
  const lo = Math.min(pair.three.price, pair.five.price);
  const hi = Math.max(end.three, end.five);
  const x = (yr) => X0 + ((X1 - X0) * yr) / HORIZON;
  const y = (v) => Y1 - ((Y1 - Y0) * (v - lo)) / (hi - lo);
  const now = story.t * HORIZON;
  const line = (k) => `M${x(0)} ${y(at(0)[k])}L${x(now)} ${y(at(now)[k])}`;
  const cur = at(now);
  const captions = hours === 8 ? watch.captions : raceCaptions(pair, hours);
  const change = (h) => { setHours(h); story.replay(); };

  return (
    <Explainer title={watch.title} captions={captions} story={story} note={watch.note} noteSrc={watch.noteSrc} sources={sources}>
      <Choice label="Hours a day you run the AC" value={hours} onChange={change}
        options={RACE_HOURS.map((h) => ({ value: h, label: `${h} hours` }))} />
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" className="block h-auto"
        aria-label={`Price plus electricity over ${HORIZON} years. ${cross != null ? `The lines cross after about ${Math.round(cross)} years.` : `They do not cross in ${HORIZON} years.`}`}>
        <line x1={X0} x2={X1} y1={Y1 + 4} y2={Y1 + 4} stroke="#e5e7eb" />
        <text x={X0} y={H - 6} fontSize="12" fill="#6b7280">Today</text>
        <text x={X1} y={H - 6} fontSize="12" fill="#6b7280" textAnchor="end">{HORIZON} years</text>
        {cross != null && now >= cross && (
          <g>
            <line x1={x(cross)} x2={x(cross)} y1={Y0} y2={Y1 + 4} stroke="#16a34a" strokeDasharray="4 4" />
            <circle cx={x(cross)} cy={y(at(cross).three)} r="5" fill="#16a34a" />
            <text x={Math.min(x(cross) + 6, X1 - 76)} y={Y0 + 4} fontSize="12" fontWeight="700" fill="#15803d">Paid back</text>
          </g>
        )}
        <path d={line('three')} fill="none" stroke="#6b7280" strokeWidth="3" strokeDasharray="7 5" />
        <path d={line('five')} fill="none" stroke="#2563eb" strokeWidth="3" />
        <circle cx={x(now)} cy={y(cur.three)} r="4.5" fill="#6b7280" />
        <circle cx={x(now)} cy={y(cur.five)} r="4.5" fill="#2563eb" />
      </svg>
      <div className="grid grid-cols-2 gap-2.5" aria-live="polite">
        {[['three', '3 Star', 'border-gray-200'], ['five', '5 Star', 'border-blue-200']].map(([k, name, border]) => (
          <div key={k} className={`grid gap-0.5 rounded-xl border bg-white p-3 ${border}`}>
            <p className="text-[14px] font-bold text-gray-900">{name} <span className="font-semibold text-gray-500">{pair[k].code}</span></p>
            <p className="text-[22px] font-extrabold tabular-nums text-gray-900">{inr100(cur[k])}</p>
            <p className="text-[13px] text-gray-600">Price and power after {Math.round(now)} {Math.round(now) === 1 ? 'year' : 'years'}</p>
          </div>
        ))}
      </div>
    </Explainer>
  );
}
